import type {
  ScheduledReportFrequency,
  ScheduledReportRule,
} from '@terra/shared-contracts-applications';
import {
  getMaxDayForMonth,
  getNextScheduledReportRunUtc,
} from '@terra/shared-utils';
import { getBrowserTimezone } from '../../automation/automationScheduleShared';

export const SCHEDULED_REPORT_FREQUENCIES = [
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'SEMI_ANNUAL',
  'YEARLY',
] as const satisfies readonly ScheduledReportFrequency[];

export const SCHEDULED_REPORT_HOUR_OPTIONS = Array.from(
  { length: 24 },
  (_, hour) => hour,
) as readonly number[];

export const DAY_OF_MONTH_OPTIONS = Array.from(
  { length: 28 },
  (_, index) => index + 1,
) as readonly number[];

export const MONTH_OPTIONS = Array.from(
  { length: 12 },
  (_, index) => index + 1,
) as readonly number[];

export const getYearlyDayOptions = (
  month: number | null | undefined,
): readonly number[] => {
  const maxDay =
    month === null || month === undefined ? 31 : getMaxDayForMonth(month);

  return Array.from({ length: maxDay }, (_, index) => index + 1);
};

export const clampYearlyDayToMonth = ({
  yearlyMonth,
  yearlyDay,
}: {
  yearlyMonth: number;
  yearlyDay: number | null | undefined;
}): number | null => {
  if (yearlyDay === null || yearlyDay === undefined) {
    return null;
  }

  const maxDay = getMaxDayForMonth(yearlyMonth);

  if (yearlyDay < 1) {
    return 1;
  }

  return yearlyDay > maxDay ? maxDay : yearlyDay;
};

const createRuleId = (): string => crypto.randomUUID();

export const createDefaultScheduledReportRule = ({
  frequency,
  timezone = getBrowserTimezone(),
}: {
  frequency: ScheduledReportFrequency;
  timezone?: string;
}): ScheduledReportRule => {
  const base = {
    id: createRuleId(),
    frequency,
    daysOfWeek: [] as number[],
    dayOfMonth: null as number | null,
    lastDayOfMonth: null as boolean | null,
    fiscalYearStartMonth: null as number | null,
    yearlyMonth: null as number | null,
    yearlyDay: null as number | null,
    hour: 9,
    timezone,
    lastRunAt: null,
    nextRunAt: null,
  };

  switch (frequency) {
    case 'WEEKLY':
      return { ...base, daysOfWeek: [] };
    case 'MONTHLY':
      return { ...base, dayOfMonth: null, lastDayOfMonth: false };
    case 'QUARTERLY':
    case 'SEMI_ANNUAL':
      return { ...base, fiscalYearStartMonth: null };
    case 'YEARLY':
      return { ...base, yearlyMonth: null, yearlyDay: null };
  }
};

export type ScheduledReportRuleFieldErrors = {
  daysOfWeek?: true;
  dayOfMonth?: true;
  fiscalYearStartMonth?: true;
  yearlyMonth?: true;
  yearlyDay?: true;
};

export const getScheduledReportRuleFieldErrors = (
  rule: ScheduledReportRule,
): ScheduledReportRuleFieldErrors => {
  const errors: ScheduledReportRuleFieldErrors = {};

  if (rule.frequency === 'WEEKLY' && rule.daysOfWeek.length === 0) {
    errors.daysOfWeek = true;
  }

  if (rule.frequency === 'MONTHLY') {
    const hasDayOfMonth =
      rule.dayOfMonth !== undefined && rule.dayOfMonth !== null;
    const hasLastDay = rule.lastDayOfMonth === true;

    if (!hasDayOfMonth && !hasLastDay) {
      errors.dayOfMonth = true;
    }
  }

  if (
    (rule.frequency === 'QUARTERLY' || rule.frequency === 'SEMI_ANNUAL') &&
    (rule.fiscalYearStartMonth === undefined ||
      rule.fiscalYearStartMonth === null)
  ) {
    errors.fiscalYearStartMonth = true;
  }

  if (rule.frequency === 'YEARLY') {
    if (rule.yearlyMonth === undefined || rule.yearlyMonth === null) {
      errors.yearlyMonth = true;
    }

    if (rule.yearlyDay === undefined || rule.yearlyDay === null) {
      errors.yearlyDay = true;
    } else if (
      rule.yearlyMonth !== undefined &&
      rule.yearlyMonth !== null &&
      rule.yearlyDay > getMaxDayForMonth(rule.yearlyMonth)
    ) {
      errors.yearlyDay = true;
    }
  }

  return errors;
};

export const hasScheduledReportRuleFieldErrors = (
  rule: ScheduledReportRule,
): boolean => Object.keys(getScheduledReportRuleFieldErrors(rule)).length > 0;

export const getPersistableScheduledReportRules = (
  rules: readonly ScheduledReportRule[],
): ScheduledReportRule[] =>
  rules.filter((rule) => !hasScheduledReportRuleFieldErrors(rule));

export const areScheduledReportRulesPersistable = (
  rules: readonly ScheduledReportRule[],
): boolean => {
  if (rules.length === 0) {
    return false;
  }

  return getPersistableScheduledReportRules(rules).length === rules.length;
};

export const formatReportFrequencySummary = ({
  rules,
  getFrequencyLabel,
}: {
  rules: ScheduledReportRule[];
  getFrequencyLabel: (frequency: ScheduledReportFrequency) => string;
}): string | null => {
  if (rules.length === 0) {
    return null;
  }

  return sortScheduledReportRules(rules)
    .map((rule) => getFrequencyLabel(rule.frequency))
    .join(' + ');
};

export const getAvailableScheduledReportFrequencies = (
  rules: ScheduledReportRule[],
): ScheduledReportFrequency[] => {
  const usedFrequencies = new Set(rules.map((rule) => rule.frequency));

  return SCHEDULED_REPORT_FREQUENCIES.filter(
    (frequency) => !usedFrequencies.has(frequency),
  );
};

export const appendRulesForFrequencies = ({
  rules,
  frequencies,
  timezone,
}: {
  rules: ScheduledReportRule[];
  frequencies: ScheduledReportFrequency[];
  timezone?: string;
}): ScheduledReportRule[] => {
  const available = new Set(getAvailableScheduledReportFrequencies(rules));
  const frequenciesToAdd = frequencies.filter((frequency) =>
    available.has(frequency),
  );

  if (frequenciesToAdd.length === 0) {
    return sortScheduledReportRules(rules);
  }

  return sortScheduledReportRules([
    ...rules,
    ...frequenciesToAdd.map((frequency) =>
      createDefaultScheduledReportRule({
        frequency,
        timezone: timezone ?? rules[0]?.timezone,
      }),
    ),
  ]);
};

export const sortScheduledReportRules = (
  rules: ScheduledReportRule[],
): ScheduledReportRule[] => {
  const order = new Map(
    SCHEDULED_REPORT_FREQUENCIES.map((frequency, index) => [frequency, index]),
  );

  return [...rules].sort((left, right) => {
    const frequencyOrder =
      (order.get(left.frequency) ?? 0) - (order.get(right.frequency) ?? 0);

    if (frequencyOrder !== 0) {
      return frequencyOrder;
    }

    return left.id.localeCompare(right.id);
  });
};

export const replaceRuleById = ({
  rules,
  nextRule,
}: {
  rules: ScheduledReportRule[];
  nextRule: ScheduledReportRule;
}): ScheduledReportRule[] =>
  sortScheduledReportRules(
    rules.map((rule) => (rule.id === nextRule.id ? nextRule : rule)),
  );

export const removeRuleById = ({
  rules,
  ruleId,
}: {
  rules: ScheduledReportRule[];
  ruleId: string;
}): ScheduledReportRule[] =>
  sortScheduledReportRules(rules.filter((rule) => rule.id !== ruleId));

export const appendDefaultScheduledReportRule = ({
  rules,
  frequency = 'WEEKLY',
  timezone,
}: {
  rules: ScheduledReportRule[];
  frequency?: ScheduledReportFrequency;
  timezone?: string;
}): ScheduledReportRule[] =>
  sortScheduledReportRules([
    ...rules,
    createDefaultScheduledReportRule({
      frequency,
      timezone: timezone ?? rules[0]?.timezone,
    }),
  ]);

export const changeRuleFrequency = ({
  rule,
  frequency,
}: {
  rule: ScheduledReportRule;
  frequency: ScheduledReportFrequency;
}): ScheduledReportRule => {
  if (rule.frequency === frequency) {
    return rule;
  }

  const defaults = createDefaultScheduledReportRule({
    frequency,
    timezone: rule.timezone,
  });

  return {
    ...defaults,
    id: rule.id,
    hour: rule.hour,
    timezone: rule.timezone,
    lastRunAt: rule.lastRunAt,
    nextRunAt: rule.nextRunAt,
  };
};

export const formatNextScheduledReportLabel = ({
  nextScheduledReportAt,
  timezone,
  locale = 'en-US',
}: {
  nextScheduledReportAt: Date | string | null | undefined;
  timezone?: string | null;
  locale?: string;
}): string | null => {
  if (!nextScheduledReportAt) {
    return null;
  }

  const date = new Date(nextScheduledReportAt);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const resolvedTimezone = timezone || 'UTC';

  try {
    const weekday = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      timeZone: resolvedTimezone,
    }).format(date);
    const monthDay = new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      timeZone: resolvedTimezone,
    }).format(date);
    const time = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: resolvedTimezone,
    }).format(date);
    const zone = new Intl.DateTimeFormat(locale, {
      timeZone: resolvedTimezone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;

    return zone
      ? `${weekday}, ${monthDay} at ${time} ${zone}`
      : `${weekday}, ${monthDay} at ${time}`;
  } catch {
    return date.toLocaleString(locale);
  }
};

export type NextScheduledReportOccurrence = {
  runAt: Date;
  timezone: string;
};

export const getSoonestScheduledReportOccurrence = (
  rules: ScheduledReportRule[],
): NextScheduledReportOccurrence | null =>
  rules
    .flatMap((rule) => {
      const runAt =
        rule.nextRunAt != null
          ? rule.nextRunAt instanceof Date
            ? rule.nextRunAt
            : new Date(rule.nextRunAt)
          : getNextScheduledReportRunUtc({ rule });

      return runAt && !Number.isNaN(runAt.getTime())
        ? [{ runAt, timezone: rule.timezone }]
        : [];
    })
    .sort((left, right) => left.runAt.getTime() - right.runAt.getTime())[0] ??
  null;

export const getSoonestRuleTimezone = (
  rules: ScheduledReportRule[],
): string | null => {
  const withNext = rules
    .filter(
      (rule): rule is ScheduledReportRule & { nextRunAt: Date } =>
        rule.nextRunAt != null,
    )
    .sort(
      (left, right) =>
        new Date(left.nextRunAt).getTime() -
        new Date(right.nextRunAt).getTime(),
    );

  return withNext[0]?.timezone ?? rules[0]?.timezone ?? null;
};
